import { html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhBroadcast";
import "../centered-dialog/wow-centered-dialog";
import "../button/wow-button";
import { SignalElement, inject } from "ui-common/di";
import type { IRpcService } from "ui-common/services/rpc/types";
import { NotificationService } from "../notification/notification-service";

import { styles } from "./go-live-button.css";

export interface GoLiveButtonTranslations {
  goLive: string;
  goingLive: string;
  goLiveSuccess: string;
  goLiveFailed: string;
  goLiveHeader: string;
  goLiveConfirm: string;
  goLiveButton: string;
  goLiveCancel: string;
}

const defaultTranslations: GoLiveButtonTranslations = {
  goLive: "Go Live",
  goingLive: "Going Live...",
  goLiveSuccess: "You are now live!",
  goLiveFailed: "Failed to go live. Please try again.",
  goLiveHeader: "Ready to go live?",
  goLiveConfirm: "You're about to make your stream accessible to your fans. Ready?",
  goLiveButton: "Start stream",
  goLiveCancel: "Cancel",
};

@customElement("go-live-button")
export class GoLiveButton extends SignalElement {
  static override styles = [styles];

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  @property({ type: String })
  eventId!: string;

  @property({ type: Object })
  translations: Partial<GoLiveButtonTranslations> = {};

  @property({ type: String })
  variant: "primary" | "danger" | "neutral" = "danger";

  @property({ type: String })
  size: "small" | "medium" | "large" = "large";

  @property({ type: Boolean, attribute: "full-width" })
  fullWidth = true;

  @property({ type: Boolean, reflect: true })
  rounded = false;

  @state()
  private isGoingLive = false;

  @state()
  private showConfirmation = false;

  private get t(): GoLiveButtonTranslations {
    return { ...defaultTranslations, ...this.translations };
  }

  private handleGoLiveClick() {
    this.showConfirmation = true;
  }

  private handleCancelGoLive() {
    this.showConfirmation = false;
  }

  private async handleConfirmGoLive() {
    if (!this.eventId) return;

    try {
      this.isGoingLive = true;
      this.showConfirmation = false;
      const result = await this.rpcService.call<{ success: boolean }>("start-live-event", { event_id: this.eventId });

      if (result.success) {
        NotificationService.success(this.t.goLiveSuccess);
        this.dispatchEvent(
          new CustomEvent("event-went-live", {
            detail: { eventId: this.eventId },
            bubbles: true,
            composed: true,
          }),
        );
      } else {
        NotificationService.error(this.t.goLiveFailed);
      }
    } catch (error) {
      console.error("Error starting live event:", error);
      NotificationService.error(this.t.goLiveFailed);
    } finally {
      this.isGoingLive = false;
    }
  }

  override render() {
    return html`
      <wow-button
        variant=${this.variant}
        size=${this.size}
        ?full-width=${this.fullWidth}
        ?rounded=${this.rounded}
        @click=${this.handleGoLiveClick}
        ?disabled=${this.isGoingLive}
      >
        <ph-broadcast slot="prefix" size="18" weight="fill"></ph-broadcast>
        <span class="go-live-label">${this.isGoingLive ? this.t.goingLive : this.t.goLive}</span>
      </wow-button>
      ${this.showConfirmation
        ? html`
            <wow-centered-dialog
              header="${this.t.goLiveHeader}"
              description="${this.t.goLiveConfirm}"
              @close=${this.handleCancelGoLive}
            >
              <wow-button variant="danger" size="large" full-width @click=${this.handleConfirmGoLive}>
                <ph-broadcast slot="prefix" size="18" weight="fill"></ph-broadcast>
                ${this.t.goLiveButton}
              </wow-button>
              <wow-button variant="neutral" size="large" full-width @click=${this.handleCancelGoLive}>
                ${this.t.goLiveCancel}
              </wow-button>
            </wow-centered-dialog>
          `
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "go-live-button": GoLiveButton;
  }
}
